import React, { useState, useEffect } from 'react';
import ClientService from '../../../services/ClientService';
import ProjectService from '../../../services/ProjectService';
import { useParams } from 'react-router-dom';

const AddClientProject = () => {
  const { clientId } = useParams(); // <-- Get clientId from URL

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(clientId);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    status: 'ACTIVE',
    startDate: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Fetch clients on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const clientsData = await ClientService.getAllClients();
        setClients(clientsData);
      } catch (error) {
        setMessage({
          text: 'Error loading clients: ' + (error.response?.data?.message || error.message),
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProjectForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    if (!selectedClientId || !projectForm.name) {
      setMessage({
        text: 'Please select a client and enter a project name',
        type: 'error'
      });
      console.log('Form validation failed. Missing client ID or project name'); // Log validation failure
      return;
    }

    try {
      setLoading(true);
      console.log('Form submission started');  // Log start of submission

      // Step 1: Prepare project data and create the project
      const projectData = {
        ...projectForm,
        clientId: selectedClientId // Send the client ID as part of project creation
      };
      console.log('Project data to be sent:', projectData);  // Log the project data being sent to the API

      const newProject = await ProjectService.createProject(projectData);
      console.log('Project created successfully:', newProject);  // Log the response of the created project

      // Step 2: Get the project ID from the response
      const projectId = newProject._id;
      console.log('Project ID received:', projectId);  // Log the project ID

      // Step 3: Add the project to the client (if needed - the API might already do this)
      try {
        await ClientService.addProject(selectedClientId, projectId);
        console.log('Project added to client successfully');  // Log success
      } catch (error) {
        // If this fails but the project was created, handle this case
        console.warn('Project created but not added to client:', error);  // Log failure to add project to client
      }

      // Success message
      setMessage({
        text: `Project "${newProject.name}" successfully created and added to client`,
        type: 'success'
      });
      console.log(`Project "${newProject.name}" successfully created and added to client`);  // Log success message

      // Reset form data
      setProjectForm({
        name: '',
        description: '',
        status: 'ACTIVE',
        startDate: new Date().toISOString().split('T')[0]
      });
      console.log('Form reset after successful submission');  // Log form reset

    } catch (error) {
      console.error('Error creating project:', error);  // Log any errors encountered during the process
      setMessage({
        text: 'Error creating project: ' + (error.response?.data?.message || error.message),
        type: 'error'
      });
    } finally {
      setLoading(false);
      console.log('Form submission finished');  // Log when submission process is complete
    }
  };


  return (
    <div className="bg-white shadow-md rounded-lg p-6 max-w-lg mx-auto">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Add Project to Client</h2>

      {message.text && (
        <div className={`p-4 mb-4 rounded-md ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="clientSelect" className="block text-gray-700 text-sm font-bold mb-2">
            Select Client
          </label>
          <select
            id="clientSelect"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            disabled={loading}
          >
            <option value="">-- Select Client --</option>
            {clients.map((client) => (
              <option key={client._id} value={client._id}>
                {client.companyName || 'Unnamed Client'} ({client.user?.name || 'Unknown User'})
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-2">
            Project Name*
          </label>
          <input
            id="name"
            name="name"
            type="text"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Enter project name"
            value={projectForm.name}
            onChange={handleInputChange}
            disabled={loading}
            required
          />
        </div>

        <div className="mb-4">
          <label htmlFor="description" className="block text-gray-700 text-sm font-bold mb-2">
            Project Description
          </label>
          <textarea
            id="description"
            name="description"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Enter project description"
            value={projectForm.description}
            onChange={handleInputChange}
            rows="3"
            disabled={loading}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="status" className="block text-gray-700 text-sm font-bold mb-2">
            Project Status
          </label>
          <select
            id="status"
            name="status"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            value={projectForm.status}
            onChange={handleInputChange}
            disabled={loading}
          >
            <option value="PLANNING">Planning</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="mb-6">
          <label htmlFor="startDate" className="block text-gray-700 text-sm font-bold mb-2">
            Start Date
          </label>
          <input
            id="startDate"
            name="startDate"
            type="date"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            value={projectForm.startDate}
            onChange={handleInputChange}
            disabled={loading}
          />
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            onClick={() => {
              setSelectedClientId('');
              setProjectForm({
                name: '',
                description: '',
                status: 'ACTIVE',
                startDate: new Date().toISOString().split('T')[0]
              });
              setMessage({ text: '', type: '' });
            }}
            disabled={loading}
          >
            Reset
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={loading || !selectedClientId || !projectForm.name}
          >
            {loading ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddClientProject;